'use client';

import { useState, useCallback, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@flowstack/sdk';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import PrivyLoginButton from './PrivyLoginButton';
import { POST_AUTH_HOME } from '@/src/lib/flags';

export default function LoginForm({ onSwitchMode }: { onSwitchMode?: () => void } = {}) {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setError('');
      setLoading(true);
      try {
        const ok = await login(email, password);
        if (ok) {
          navigate(POST_AUTH_HOME);
        } else {
          setError('Incorrect email or password.');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Login failed. Check your credentials.');
      } finally {
        setLoading(false);
      }
    },
    [email, password, login, navigate],
  );

  return (
    <div className="w-full max-w-sm mx-auto">
      <div className="mb-8 text-center">
        <h1 className="text-2xl font-semibold text-[var(--color-text)] mb-1">Welcome back</h1>
        <p className="text-sm text-[var(--color-text-secondary)]">Sign in to your Casino account</p>
      </div>

      {/* Primary: Privy embedded wallet login */}
      <PrivyLoginButton label="Sign in" className="mb-4" />

      {/* Secondary: email/password — collapsed by default */}
      {!showEmailForm ? (
        <button
          type="button"
          onClick={() => setShowEmailForm(true)}
          className="w-full mt-3 text-xs text-[var(--color-text-tertiary)] hover:text-[var(--color-text-secondary)] transition-colors"
        >
          Sign in with email &amp; password
        </button>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 mt-4">
          <Input
            id="email"
            type="email"
            label="Email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
          <Input
            id="password"
            type="password"
            label="Password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
          />

          {error && (
            <p className="text-sm text-[var(--color-error)] bg-[var(--color-error-light)] px-3 py-2 rounded-lg">
              {error}
            </p>
          )}

          <Button type="submit" loading={loading} size="lg" className="w-full">
            Sign in
          </Button>
        </form>
      )}

      <p className="mt-6 text-center text-sm text-[var(--color-text-secondary)]">
        Don&apos;t have an account?{' '}
        {onSwitchMode ? (
          <button type="button" onClick={onSwitchMode} className="text-[var(--color-accent)] hover:underline font-medium">
            Create one
          </button>
        ) : (
          <Link to="/register" className="text-[var(--color-accent)] hover:underline font-medium">
            Create one
          </Link>
        )}
      </p>
    </div>
  );
}
